import { useMemo } from "react";
import { Platform } from "react-native";
import { useQuery } from "@tanstack/react-query";
import { COUNTRIES } from "./countryMetadata";
import {
  resolveCountryPreference,
  type CountryPreferenceSource,
} from "./countryPreference";
import { getDeviceCountryCode } from "./deviceCountry";
import { getIpCountryCodeWithTimeout } from "./ipCountry";

const ALL_COUNTRY_CODES = COUNTRIES.map((country) => country.code);

export type DetectedCountry = {
  countryCode: string;
  source: CountryPreferenceSource;
  /** True while the IP answer is still out; until then the device answers. */
  isDetecting: boolean;
};

export type UseDetectedCountryOptions = {
  /** A country already on record. It wins whenever it is supported. */
  savedCountry?: string | null;
  /** Pass a module-level constant; the default is every known country. */
  supportedCountryCodes?: readonly string[];
  fallbackCountry?: string;
  /** False keeps the network quiet; the device region still answers. */
  enabled?: boolean;
};

/**
 * The one chain for a country nobody has declared: saved, then IP, then the
 * device region, then the fallback. The IP lookup is a query cached for the
 * session, so the Location step, the phone pickers and the sign-in default
 * share one network call instead of each making their own. It runs on web
 * only, where the browser's location is the one signal we have; a native
 * device carries a region of its own.
 */
export function useDetectedCountry({
  savedCountry,
  supportedCountryCodes = ALL_COUNTRY_CODES,
  fallbackCountry = "US",
  enabled = true,
}: UseDetectedCountryOptions = {}): DetectedCountry {
  const localeCountry = useMemo(getDeviceCountryCode, []);
  const saved = resolveCountryPreference({
    savedCountry,
    supportedCountryCodes,
    fallbackCountry,
  });
  const lookupEnabled =
    enabled && saved.source !== "saved" && Platform.OS === "web";
  const ipLookup = useQuery({
    queryKey: ["ip-country"],
    queryFn: async ({ signal }) => {
      const country = await getIpCountryCodeWithTimeout({ signal });
      // A miss is not an answer. The lookup service has slow minutes, and a
      // cached miss would decide the whole session; left unrecorded, the next
      // surface that asks simply tries again.
      if (!country) throw new Error("IP country lookup did not answer");
      return country;
    },
    enabled: lookupEnabled,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });

  const resolved = resolveCountryPreference({
    savedCountry,
    ipCountry: ipLookup.data,
    localeCountry,
    supportedCountryCodes,
    fallbackCountry,
  });
  return { ...resolved, isDetecting: lookupEnabled && ipLookup.isPending };
}
